//ESAutocompleteSearch.tsx

import React, { useState, useEffect, useRef, useCallback } from "react";
import { Form, ListGroup, Spinner, InputGroup, Button } from "react-bootstrap";
import { axiosPost } from "../utils/AxiosHelper";
import searchico from "../assets/images/search.svg";
import "./ESAutocompleteSearch.scss";

interface Props {
    apiUrl: string;
    placeholder?: string;
    labelKey: string;
    valueKey: string;
    initialValue?: string;
    onSearchChange?: (text: string) => void;
    onSelect?: (item: any) => void;
    onSubmit?: () => void;
    otherParams?: any;
    excludeItems?: any[];
    className?: string;
    hideIcon?: boolean;
    searchKey?: string;
}

const ESAutocompleteSearch: React.FC<Props> = ({
    apiUrl,
    placeholder = "Search...",
    labelKey,
    valueKey,
    initialValue = "",
    onSearchChange,
    onSelect,
    onSubmit,
    otherParams = {},
    className = "",
    excludeItems = [],
    hideIcon = false,
    searchKey = "SearchText",
}) => {
    const [queryString, setQueryString] = useState(initialValue);
    const [suggestions, setSuggestions] = useState<any[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [hasSearched, setHasSearched] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const suggestionRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const lastFetchedQuery = useRef("");

    // Sync with initialValue if it changes from parent
    useEffect(() => {
        setQueryString(initialValue);
        if (!initialValue) {
            setHasSearched(false);
            setSuggestions([]);
            setPage(1);
            setHasMore(true);
        }
    }, [initialValue]);

    const fetchSuggestions = useCallback(async (search: string, pageNumber: number = 1) => {
        if (!search || search.trim().length === 0) {
            setSuggestions([]);
            setShowSuggestions(false);
            setHasSearched(false);
            lastFetchedQuery.current = "";
            return;
        }

        if (pageNumber === 1) {
            setIsLoading(true);
            setShowSuggestions(true);
        } else {
            setIsFetchingMore(true);
        }

        setHasSearched(true);
        try {
            const response: any = await axiosPost(apiUrl, {
                [searchKey]: search,
                CurrentPageNo: pageNumber,
                PageSize: 10,
                SortDir: "ASC",
                SortBy: labelKey,
                ...otherParams,
            });

            let data = response?.data?.ExtraData || [];

            if (excludeItems && excludeItems.length > 0) {
                data = data.filter((item: any) => 
                    !excludeItems.some((ex: any) => ex[valueKey] === item[valueKey])
                );
            }

            if (pageNumber === 1) {
                setSuggestions(data);
            } else {
                setSuggestions(prev => [...prev, ...data]);
            }

            setHasMore(data.length === 10);
            lastFetchedQuery.current = search;
        } catch (error) {
            console.error("Error fetching suggestions:", error);
            if (pageNumber === 1) setSuggestions([]);
        } finally {
            setIsLoading(false);
            setIsFetchingMore(false);
        }
    }, [apiUrl, labelKey, JSON.stringify(otherParams), excludeItems, valueKey]);

    useEffect(() => {
        const handler = setTimeout(() => {
            if (queryString.trim().length > 0 && queryString !== lastFetchedQuery.current) {
                setPage(1);
                setHasMore(true);
                fetchSuggestions(queryString, 1);
            }
        }, 300);

        return () => clearTimeout(handler);
    }, [queryString, fetchSuggestions]);

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const target = e.currentTarget;
        if (
            !isLoading &&
            !isFetchingMore &&
            hasMore &&
            target.scrollHeight - target.scrollTop <= target.clientHeight + 10 // Threshold
        ) {
            const nextPage = page + 1;
            setPage(nextPage);
            fetchSuggestions(queryString, nextPage);
        }
    };

    // Handle click outside to close suggestions
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                suggestionRef.current &&
                !suggestionRef.current.contains(event.target as Node) &&
                inputRef.current &&
                !inputRef.current.contains(event.target as Node)
            ) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setQueryString(value);
        onSearchChange && onSearchChange(value);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIndex((prev) => (prev > 0 ? prev - 1 : -1));
        } else if (e.key === "Enter") {
            e.preventDefault();
            if (activeIndex >= 0 && activeIndex < suggestions.length) {
                handleSelect(suggestions[activeIndex]);
            } else {
                onSubmit && onSubmit();
                setShowSuggestions(false);
            }
        } else if (e.key === "Escape") {
            setShowSuggestions(false);
        }
    };

    const handleSelect = (item: any) => {
        const selectedLabel = item[labelKey];
        setQueryString(selectedLabel);
        lastFetchedQuery.current = selectedLabel; // Prevent re-triggering search
        onSearchChange && onSearchChange(selectedLabel);
        onSelect && onSelect(item);
        setShowSuggestions(false);
        setActiveIndex(-1);
    };

    return (
        <div className={`es-autocomplete-container ${className}`}>
            <InputGroup className="search-input-group">
                <Form.Control
                    ref={inputRef}
                    type="text"
                    placeholder={placeholder}
                    value={queryString}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onFocus={() => queryString.length > 0 && setShowSuggestions(true)}
                    autoComplete="off"
                />
                {!hideIcon && (
                    <Button className="btn-search" onClick={() => onSubmit && onSubmit()}>
                        <img src={searchico} alt="search" className="search-icon" />
                    </Button>
                )}
            </InputGroup>

            {showSuggestions && (queryString.length > 0) && (
                <div className="suggestions-dropdown" ref={suggestionRef} onScroll={handleScroll}>
                    <ListGroup>
                        {isLoading ? (
                            <ListGroup.Item className="text-center py-3 text-muted">
                                <Spinner animation="border" size="sm" variant="primary" className="me-2" />
                                Searching...
                            </ListGroup.Item>
                        ) : suggestions.length > 0 ? (
                            <>
                                {suggestions.map((item, index) => (
                                    <ListGroup.Item
                                        key={item[valueKey] || index}
                                        action
                                        onClick={() => handleSelect(item)}
                                        active={index === activeIndex}
                                    >
                                        {item[labelKey]}
                                    </ListGroup.Item>
                                ))}
                                {isFetchingMore && (
                                    <ListGroup.Item className="text-center py-2 text-muted">
                                        <Spinner animation="border" size="sm" variant="primary" className="me-2" />
                                        Loading more...
                                    </ListGroup.Item>
                                )}
                            </>
                        ) : hasSearched ? (
                            <ListGroup.Item className="text-center py-3 text-muted">
                                No suggestions found
                            </ListGroup.Item>
                        ) : null}
                    </ListGroup>
                </div>
            )}
        </div>
    );
};

export default ESAutocompleteSearch;
